import { useEffect, useState } from "react";
import { api } from "@/lib/session";
import { Pencil, Trash2, Plus, Check, X } from "lucide-react";

interface DecisionMaker {
  id: number;
  accountId: number;
  name: string;
  title: string;
  strength: number;
}

const STRENGTH_LABEL: Record<number, string> = {
  1: "1 — cold",
  2: "2 — aware",
  3: "3 — engaged",
  4: "4 — supportive",
  5: "5 — champion",
};

type Notice = { kind: "error" | "info" | "success"; text: string } | null;

export default function DecisionMakerPanel({ accountId }: { accountId: number }) {
  const [list, setList] = useState<DecisionMaker[]>([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<Notice>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  // Add-form state
  const [addName, setAddName] = useState("");
  const [addTitle, setAddTitle] = useState("");
  const [addStrength, setAddStrength] = useState(1);

  // Edit-form state
  const [editName, setEditName] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editStrength, setEditStrength] = useState(1);

  async function refresh() {
    try {
      const dms = await api<DecisionMaker[]>(`/api/accounts/${accountId}/decision-makers`);
      setList(dms);
    } catch (e: any) {
      setNotice({ kind: "error", text: e?.message || "Failed to load decision makers" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  function resetAddForm() {
    setAddName("");
    setAddTitle("");
    setAddStrength(1);
  }

  async function onAdd(e: React.FormEvent) {
    e.preventDefault();
    const name = addName.trim();
    if (!name) {
      setNotice({ kind: "error", text: "Name is required." });
      return;
    }
    setBusy(true);
    try {
      await api(`/api/accounts/${accountId}/decision-makers`, {
        method: "POST",
        body: JSON.stringify({
          name,
          title: addTitle.trim(),
          strength: addStrength,
        }),
      });
      resetAddForm();
      setShowAdd(false);
      setNotice({ kind: "success", text: "Decision maker added." });
      await refresh();
    } catch (e: any) {
      setNotice({ kind: "error", text: e?.message || "Save failed" });
    } finally {
      setBusy(false);
    }
  }

  function startEdit(dm: DecisionMaker) {
    setEditingId(dm.id);
    setEditName(dm.name);
    setEditTitle(dm.title ?? "");
    setEditStrength(dm.strength ?? 1);
  }

  function cancelEdit() {
    setEditingId(null);
  }

  async function saveEdit(id: number) {
    const name = editName.trim();
    if (!name) {
      setNotice({ kind: "error", text: "Name is required." });
      return;
    }
    setBusy(true);
    try {
      await api(`/api/decision-makers/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name,
          title: editTitle.trim(),
          strength: editStrength,
        }),
      });
      setEditingId(null);
      setNotice({ kind: "success", text: "Decision maker updated." });
      await refresh();
    } catch (e: any) {
      setNotice({ kind: "error", text: e?.message || "Save failed" });
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(dm: DecisionMaker) {
    const ok = window.confirm(`Remove ${dm.name} from this account?`);
    if (!ok) return;
    setBusy(true);
    try {
      await api(`/api/decision-makers/${dm.id}`, { method: "DELETE" });
      setNotice({ kind: "success", text: "Decision maker removed." });
      await refresh();
    } catch (e: any) {
      setNotice({ kind: "error", text: e?.message || "Delete failed" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="border border-border rounded-md bg-[hsl(var(--surface))] p-5 mb-6"
      data-testid="dm-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            Stakeholders
          </div>
          <h2 className="text-lg font-semibold tracking-tight">Decision makers</h2>
        </div>
        {!showAdd && (
          <button
            type="button"
            onClick={() => { setShowAdd(true); setNotice(null); }}
            className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition-colors"
            data-testid="dm-add-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add decision maker
          </button>
        )}
      </div>

      {notice && (
        <div
          role="status"
          className={`mb-3 px-3 py-2 rounded text-[12px] border ${
            notice.kind === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : notice.kind === "error"
                ? "bg-rose-50 border-rose-200 text-rose-800"
                : "bg-orange-50 border-orange-200 text-yellow-800"
          }`}
          data-testid="dm-notice"
        >
          {notice.text}
        </div>
      )}

      {showAdd && (
        <form
          onSubmit={onAdd}
          className="mb-4 p-4 rounded border border-border bg-background space-y-3"
          data-testid="dm-add-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Name
              </span>
              <input
                type="text"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                required
                autoFocus
                className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                data-testid="dm-add-name"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Title (optional)
              </span>
              <input
                type="text"
                value={addTitle}
                onChange={(e) => setAddTitle(e.target.value)}
                className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                data-testid="dm-add-title"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Relationship strength: 1=cold, 5=champion
              </span>
              <select
                value={addStrength}
                onChange={(e) => setAddStrength(Number(e.target.value))}
                className="mt-1 w-full text-[13px] px-2.5 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                data-testid="dm-add-strength"
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>{STRENGTH_LABEL[n]}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={busy}
              className="text-[12px] px-3 py-1.5 rounded border bg-foreground text-background border-foreground hover:opacity-90 disabled:opacity-50 transition"
              data-testid="dm-add-save"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); resetAddForm(); }}
              disabled={busy}
              className="text-[12px] px-3 py-1.5 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition"
              data-testid="dm-add-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-[12px] text-muted-foreground">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-[13px] text-muted-foreground py-2" data-testid="dm-empty">
          No decision makers added yet. Add the people involved in this deal.
        </div>
      ) : (
        <div className="border border-border rounded overflow-hidden">
          <table className="w-full text-[13px]" data-testid="dm-table">
            <thead className="bg-[hsl(var(--surface-2))] text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-3 py-2 w-[32%]">Name</th>
                <th className="text-left font-medium px-3 py-2 w-[36%]">Title</th>
                <th className="text-left font-medium px-3 py-2 w-[20%]">Strength</th>
                <th className="text-right font-medium px-3 py-2 w-[12%]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-background">
              {list.map((dm) => {
                const isEditing = editingId === dm.id;
                if (isEditing) {
                  return (
                    <tr key={dm.id} data-testid={`dm-row-${dm.id}`}>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-full text-[13px] px-2 py-1 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                          data-testid="dm-edit-name"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="w-full text-[13px] px-2 py-1 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                          data-testid="dm-edit-title"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={editStrength}
                          onChange={(e) => setEditStrength(Number(e.target.value))}
                          className="w-full text-[13px] px-2 py-1 rounded border border-border bg-background focus:outline-none focus:border-foreground"
                          data-testid="dm-edit-strength"
                        >
                          {[1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>{STRENGTH_LABEL[n]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => saveEdit(dm.id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded border bg-foreground text-background border-foreground hover:opacity-90 disabled:opacity-50 transition mr-1"
                          data-testid={`dm-save-${dm.id}`}
                          aria-label="Save"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={busy}
                          className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition"
                          data-testid={`dm-cancel-${dm.id}`}
                          aria-label="Cancel"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={dm.id} data-testid={`dm-row-${dm.id}`}>
                    <td className="px-3 py-2 font-medium">{dm.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {dm.title?.trim() ? dm.title : <span className="opacity-50">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <StrengthPip strength={dm.strength ?? 1} />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => startEdit(dm)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded border border-border bg-background hover:bg-[hsl(var(--surface-2))] transition mr-1"
                        data-testid={`dm-edit-${dm.id}`}
                        aria-label="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemove(dm)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded border border-rose-200 text-rose-700 bg-rose-50/50 hover:bg-rose-50 transition"
                        data-testid={`dm-remove-${dm.id}`}
                        aria-label="Remove"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StrengthPip({ strength }: { strength: number }) {
  const clamped = Math.max(1, Math.min(5, strength));
  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={STRENGTH_LABEL[clamped]}
      data-testid={`dm-strength-${clamped}`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`w-2 h-2 rounded-full border ${
            n <= clamped
              ? "bg-foreground border-foreground"
              : "bg-transparent border-border"
          }`}
          aria-hidden="true"
        />
      ))}
      <span className="text-[11px] text-muted-foreground tabular-nums ml-1">{clamped}/5</span>
    </span>
  );
}
